import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, FileText, X, CheckCircle2, AlertCircle, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import AlertBanner from "../components/AlertBanner";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import api from "../lib/api";

const ALLOWED_EXT = ["eml", "msg", "p7m", "xml", "zip"];
const MAX_SIZE = 25 * 1024 * 1024;

function extOf(name) {
    if (!name || !name.includes(".")) return "";
    return name.split(".").pop().toLowerCase();
}

function formatSize(b) {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

export default function PecUploadPage() {
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const [items, setItems] = useState([]); // {id, file, status: idle|uploading|done|error, progress, error, pecId}
    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);

    const addFiles = (fileList) => {
        const now = Date.now();
        const next = Array.from(fileList).map((f, i) => {
            const ext = extOf(f.name);
            let status = "idle";
            let error = null;
            if (!ALLOWED_EXT.includes(ext)) {
                status = "error";
                error = `Estensione .${ext || "?"} non supportata`;
            } else if (f.size > MAX_SIZE) {
                status = "error";
                error = "File oltre 25 MB";
            } else if (f.size === 0) {
                status = "error";
                error = "File vuoto";
            }
            return {
                id: `${now}-${i}-${f.name}`,
                file: f,
                ext,
                status,
                progress: 0,
                error,
                pecId: null,
                innerIds: [],
                innerErrors: [],
            };
        });
        setItems((prev) => [...prev, ...next]);
    };

    const onSelect = (ev) => {
        if (ev.target.files) addFiles(ev.target.files);
        ev.target.value = "";
    };

    const onDrop = (ev) => {
        ev.preventDefault();
        setDragOver(false);
        if (ev.dataTransfer?.files) addFiles(ev.dataTransfer.files);
    };

    const removeItem = (id) => {
        setItems((prev) => prev.filter((i) => i.id !== id));
    };

    const startUpload = async () => {
        const toUpload = items.filter((i) => i.status === "idle");
        if (toUpload.length === 0) return;

        setUploading(true);
        for (const it of toUpload) {
            const fd = new FormData();
            fd.append("files", it.file);
            setItems((prev) => prev.map((p) => (p.id === it.id ? { ...p, status: "uploading", progress: 5 } : p)));
            try {
                const { data } = await api.post("/pec/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                    onUploadProgress: (e) => {
                        const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 40;
                        setItems((prev) => prev.map((p) => (p.id === it.id ? { ...p, progress: pct } : p)));
                    },
                });
                const r = data?.results?.[0];
                if (r?.ok) {
                    setItems((prev) =>
                        prev.map((p) =>
                            p.id === it.id
                                ? {
                                    ...p,
                                    status: "done",
                                    progress: 100,
                                    pecId: r.pec_id || null,
                                    innerIds: r.pec_ids || [],
                                    innerErrors: r.errors || [],
                                }
                                : p
                        )
                    );
                } else {
                    setItems((prev) => prev.map((p) => (p.id === it.id ? { ...p, status: "error", error: r?.error || "Errore" } : p)));
                }
            } catch (err) {
                const msg = err?.response?.data?.detail || err.message || "Errore upload";
                setItems((prev) => prev.map((p) => (p.id === it.id ? { ...p, status: "error", error: msg } : p)));
            }
        }
        setUploading(false);
    };

    const doneCount = items.filter((i) => i.status === "done").length;
    const firstDone = items.find((i) => i.status === "done" && i.pecId);

    return (
        <div className="space-y-6">
            <PageHeader
                title="Analizza nuova PEC"
                subtitle="Carica uno o più file .eml, .msg, .p7m, .xml o .zip. La pipeline di analisi si avvia automaticamente."
                breadcrumbs={[{ label: "PEC" }, { label: "Nuova analisi" }]}
                actions={
                    firstDone && (
                        <Button
                            data-testid="go-to-first-analysis-btn"
                            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white gap-2"
                            onClick={() => navigate(`/pec/analyses/${firstDone.pecId}`)}
                        >
                            Apri analisi
                            <ArrowRight className="w-4 h-4" />
                        </Button>
                    )
                }
            />

            <AlertBanner variant="info" title="Sicurezza">
                I file vengono conservati intatti. La firma S/MIME e la busta PEC vengono verificate tecnicamente.
                Le analisi AI di contenuto sono in preparazione per la Fase 3.
            </AlertBanner>

            {/* Drop zone */}
            <Card
                data-testid="upload-dropzone"
                className={`border-2 border-dashed rounded-2xl transition-colors p-10 text-center cursor-pointer ${dragOver
                    ? "border-[#2563EB] bg-[#2563EB]/5"
                    : "border-[#DCE3EE] bg-white hover:border-[#2563EB]/60 hover:bg-[#F5F7FB]"
                    }`}
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
            >
                <input
                    ref={inputRef}
                    type="file"
                    className="hidden"
                    multiple
                    accept=".eml,.msg,.p7m,.xml,.zip"
                    onChange={onSelect}
                    data-testid="upload-file-input"
                />
                <div className="w-14 h-14 rounded-full bg-[#2563EB]/10 flex items-center justify-center mx-auto mb-4">
                    <UploadCloud className="w-7 h-7 text-[#2563EB]" strokeWidth={1.75} />
                </div>
                <h3 className="text-lg font-semibold text-[#172033]">
                    Trascina qui i file o clicca per selezionare
                </h3>
                <p className="text-sm text-[#64748B] mt-2">
                    Formati ammessi: <span className="font-medium">.eml, .msg, .p7m, .xml, .zip</span> · Max 25 MB per file
                </p>
                <div className="mt-4 flex items-center justify-center gap-2 text-xs text-[#64748B]">
                    <span className="px-2 py-1 rounded-full bg-[#16A34A]/10 text-[#16A34A] font-medium">.eml completo</span>
                    <span className="px-2 py-1 rounded-full bg-[#F59E0B]/10 text-[#B45309] font-medium">.msg / .p7m / .xml — placeholder</span>
                </div>
            </Card>

            {items.length > 0 && (
                <div className="space-y-3" data-testid="upload-file-list">
                    <div className="flex items-center justify-between">
                        <h2 className="text-sm font-semibold text-[#172033]">
                            File selezionati ({items.length})
                        </h2>
                        <div className="flex items-center gap-2">
                            {doneCount > 0 && (
                                <span className="text-xs text-[#16A34A] font-medium">
                                    {doneCount} completati
                                </span>
                            )}
                            <Button
                                data-testid="upload-clear-btn"
                                variant="ghost"
                                size="sm"
                                onClick={() => setItems([])}
                            >
                                Pulisci
                            </Button>
                            <Button
                                data-testid="upload-start-btn"
                                onClick={startUpload}
                                disabled={uploading || items.every((i) => i.status !== "idle")}
                                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white gap-2"
                            >
                                {uploading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Caricamento...
                                    </>
                                ) : (
                                    <>
                                        <UploadCloud className="w-4 h-4" />
                                        Avvia analisi
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                    <div className="bg-white border border-[#DCE3EE] rounded-xl divide-y divide-[#DCE3EE]">
                        {items.map((it) => (
                            <div key={it.id} className="p-4 flex items-start gap-4" data-testid={`upload-item-${it.id}`}>
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${it.status === "done"
                                    ? "bg-[#16A34A]/10 text-[#16A34A]"
                                    : it.status === "error"
                                        ? "bg-[#DC2626]/10 text-[#DC2626]"
                                        : it.status === "uploading"
                                            ? "bg-[#2563EB]/10 text-[#2563EB]"
                                            : "bg-slate-100 text-[#64748B]"
                                    }`}>
                                    {it.status === "done" ? <CheckCircle2 className="w-5 h-5" /> :
                                        it.status === "error" ? <AlertCircle className="w-5 h-5" /> :
                                            it.status === "uploading" ? <Loader2 className="w-5 h-5 animate-spin" /> :
                                                <FileText className="w-5 h-5" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <div className="text-sm font-medium text-[#172033] truncate">
                                            {it.file.name}
                                        </div>
                                        <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F5F7FB] text-[#64748B]">
                                            .{it.ext}
                                        </span>
                                    </div>
                                    <div className="text-xs text-[#64748B] mt-1">
                                        {formatSize(it.file.size)} ·{" "}
                                        {it.status === "done" && "Analisi avviata"}
                                        {it.status === "error" && <span className="text-[#DC2626]">{it.error}</span>}
                                        {it.status === "uploading" && "Caricamento in corso..."}
                                        {it.status === "idle" && "In coda"}
                                    </div>
                                    {(it.status === "uploading" || it.status === "done") && (
                                        <Progress value={it.progress} className="h-1 mt-2" />
                                    )}
                                    {it.innerIds && it.innerIds.length > 0 && (
                                        <div className="mt-2 text-xs text-[#64748B]">
                                            ZIP: {it.innerIds.length} PEC estratte
                                            {it.innerErrors.length > 0 && (
                                                <span className="text-[#F59E0B]"> · {it.innerErrors.length} scartate</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    {it.status === "done" && it.pecId && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            data-testid={`open-analysis-${it.pecId}`}
                                            onClick={() => navigate(`/pec/analyses/${it.pecId}`)}
                                        >
                                            Apri
                                        </Button>
                                    )}
                                    {it.status !== "uploading" && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            data-testid={`remove-item-${it.id}`}
                                            onClick={() => removeItem(it.id)}
                                            className="text-[#64748B] hover:text-[#DC2626]"
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
